import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { RepeatButton } from '../../../../components/repeat-button';
import { useIsDocumentUploadEnabled } from '../../../../feature-flags/hooks/use-is-document-upload-enabled';
import Global from '../../../../store/global-selectors';
import { selectCustomsDocumentsPendingDocuments } from '../../../../store/customs-documents/customs-documents.selectors';
import { savePendingDocumentConfigRequest } from '../../../../store/customs-documents/customs-documents.actions';
import { useModal } from '../../../../hooks/use-modal';
import { useCustomsDocuments } from '../../../../orders/order-details/fulfillment-plan/sections/customs-documents/use-customs-documents';
import { CustomsDocumentUploadModal } from '../../../../orders/order-details/fulfillment-plan/sections/customs-documents/customs-document-upload-modal';
import { getIncompatibleDocumentTypeLabels } from '../../../../orders/order-details/fulfillment-plan/sections/customs-documents/document-type-dropdown';
import {
  CUSTOMS_DOCUMENTS_HELP_URL,
  messages,
} from '../../../../orders/order-details/fulfillment-plan/sections/customs-documents/messages';
import style from './customs-documents-upload-subsection.module.less';

type CustomsDocumentsUploadSubsectionProps = {
  fulfillmentPlanId: string;
};

/**
 * Pre-ship customs document upload subsection rendered inside the Customs Declarations
 * sidebar section. Only rendered when readOnly=false (pre-ship) from customs-section.js.
 * Post-ship upload is handled separately in the Shipment Details modal.
 */
export const CustomsDocumentsUploadSubsection = ({ fulfillmentPlanId }: CustomsDocumentsUploadSubsectionProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const isDocumentUploadEnabled = useIsDocumentUploadEnabled();
  const { isOpen: isModalOpen, open: openModal, close: closeModal } = useModal();

  const salesOrder = useSelector(
    (state) => Global.selectEntitiesSalesOrdersByFulfillmentPlanIds(state, { fulfillmentPlanIds: [fulfillmentPlanId] })[0],
  );
  const salesOrderId = salesOrder?.salesOrderId ?? null;

  // Our store is the source of truth after user toggles; CSW state is the fallback for initial load.
  const pendingFromOurStore = useSelector((state) => selectCustomsDocumentsPendingDocuments(state, fulfillmentPlanId));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const customs = useSelector((state: any) => Global.selectCSWCustoms(state, { fulfillmentPlanId }));
  const isDeferringUpload = pendingFromOurStore ?? customs?.pendingDocuments ?? false;

  const { isLoadingCapabilities, isCarrierSupported, availableDocumentTypes, orderDocuments, handleRefresh } = useCustomsDocuments({
    salesOrderId,
    fulfillmentPlanId,
  });

  const incompatibleTypeLabels = getIncompatibleDocumentTypeLabels(intl, orderDocuments, availableDocumentTypes);

  const handleDeferChange = useCallback(
    (checked: boolean) => {
      dispatch(savePendingDocumentConfigRequest(fulfillmentPlanId, checked));
    },
    [dispatch, fulfillmentPlanId],
  );

  if (!isDocumentUploadEnabled || isLoadingCapabilities) return null;

  return (
    <FlexColumn className={style['upload-subsection']} gap={Gap.Space12}>
      <FlexRow className={style['section-header']} gap={Gap.Space8}>
        <Text textType={TextType.H4} value={messages.uploadSubsectionTitle} />
        <RepeatButton
          actionHandler={handleRefresh}
          ariaLabelMessageDescriptor={messages.refreshAriaLabel}
          className={style['refresh-icon']}
        />
      </FlexRow>
      {!isCarrierSupported && (
        <BannerMessageV2
          content={<Text textType={TextType.Paragraph} value={messages.carrierNotSupported} />}
          layout={BannerLayout.Default}
          status={BannerStatus.Warning}
        />
      )}
      {isCarrierSupported && incompatibleTypeLabels.length > 0 && (
        <BannerMessageV2
          content={
            <Text
              formattedMessageValues={{ types: incompatibleTypeLabels.join(', ') }}
              textType={TextType.Paragraph}
              value={messages.documentTypesNotSupported}
            />
          }
          layout={BannerLayout.Default}
          status={BannerStatus.Warning}
        />
      )}
      {!isDeferringUpload && (
        <FlexColumn className={style['starter-content']} gap={Gap.Space8}>
          <Text
            className={style['starter-description']}
            textType={TextType.Paragraph}
            value={messages.starterDescription}
            formattedMessageValues={{
              learnMoreLink: (chunks) => (
                <Button href={CUSTOMS_DOCUMENTS_HELP_URL} target='_blank' rel='noopener noreferrer' theme={THEME_CONSTANTS.link}>
                  {chunks}
                </Button>
              ),
            }}
          />
          <Button disabled={!isCarrierSupported} onClick={openModal} theme={THEME_CONSTANTS.secondary}>
            <FormattedMessage {...messages.starterUploadFileButton} />
          </Button>
        </FlexColumn>
      )}
      {isModalOpen && (
        <CustomsDocumentUploadModal
          availableDocumentTypes={availableDocumentTypes}
          isDisabled={!isCarrierSupported}
          onClose={closeModal}
          onRefresh={handleRefresh}
          salesOrderId={salesOrderId}
        />
      )}
      <Checkbox checked={isDeferringUpload} label={intl.formatMessage(messages.pendingDocumentsCheckbox)} onChange={handleDeferChange} />
    </FlexColumn>
  );
};
